<div class="text-fg w-full max-w-5xl" (click)="onDocumentClick($event)">
  <!-- Header -->
  <header class="mb-5">
    <div>
      <button type="button" class="cursor-pointer border-0 bg-transparent p-0 text-xs text-muted hover:text-fg" (click)="goBack()">&larr; Back</button>
      <h1 class="mb-0 mt-1.5 font-heading text-2xl font-extrabold tracking-tight text-fg">New Operation</h1>
    </div>
  </header>

  <!-- Error / Success -->
  @if (submitError(); as err) {
    <ui-alert variant="danger">
      <div class="flex items-center justify-between gap-3">
        <span>{{ err }}</span>
        <button type="button" class="cursor-pointer border-0 bg-transparent text-lg leading-none text-current" (click)="submitError.set(null)">&times;</button>
      </div>
    </ui-alert>
  }

  <!-- General Section -->
  <section class="mb-4 rounded-lg border border-border bg-card p-5">
    <h3 class="mb-3.5 mt-0 border-b border-border pb-2 text-sm font-bold uppercase tracking-wide text-muted">General Information</h3>
    <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div class="flex flex-col md:col-span-2">
        <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">Description <span class="text-danger">*</span></label>
        <input
          type="text"
          uiInput
          placeholder="Describe the purpose of this operation..."
          [(ngModel)]="description"
        />
      </div>
      <div class="flex flex-col">
        <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">Event Type <span class="text-danger">*</span></label>
        <select uiInput [(ngModel)]="eventType">
          @for (et of eventTypes; track et.value) {
            <option [value]="et.value">{{ et.label }}</option>
          }
        </select>
      </div>
    </div>
  </section>

  <!-- Debit Lines Section -->
  <section class="mb-4 rounded-lg border border-l-[3px] border-border border-l-danger bg-card p-5">
    <div class="mb-3.5 flex items-center justify-between border-b border-border pb-2">
      <h3 class="m-0 text-sm font-bold uppercase tracking-wide text-danger-fg">Debit Lines</h3>
      <button type="button" uiButton variant="outline" size="sm" (click)="addDebitLine()">
        <ng-icon name="lucidePlus" size="14" />
        <span>Add Line</span>
      </button>
    </div>

    <div class="flex flex-col gap-2.5">
      @for (line of debitLines(); track line.uid) {
        <div
          class="relative grid grid-cols-1 items-end gap-1.5 rounded-md border p-3 transition-colors md:grid-cols-[1fr_140px_1fr_32px] md:gap-2"
          [class]="line.balanceWarning ? 'border-warning bg-warning-bg' : 'border-border bg-surface-muted'"
        >
          <!-- Account Autocomplete -->
          <div class="relative flex min-w-0 flex-col">
            <label class="mb-0.5 text-[0.62rem] font-semibold uppercase tracking-wide text-muted">Account</label>
            @if (line.account) {
              <div class="flex min-h-[34px] flex-wrap items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1.5 text-xs">
                <span class="font-mono text-[0.72rem] font-bold text-fg">{{ line.account.code }}</span>
                <span class="max-w-30 truncate text-[0.7rem] text-muted">{{ line.account.name }}</span>
                <ui-badge variant="info" class="uppercase">{{ line.account.currency_code }}</ui-badge>
                <span
                  class="ml-auto font-mono text-[0.65rem]"
                  [class]="line.balanceWarning ? 'font-semibold text-warning' : 'text-muted'"
                >
                  Bal: {{ line.account.balance }}
                </span>
                <button type="button" class="ml-1 cursor-pointer border-0 bg-transparent p-0 leading-none text-muted hover:text-danger" (click)="clearAccount(line)">
                  <ng-icon name="lucideX" size="12" />
                </button>
              </div>
            } @else {
              <div class="relative">
                <ng-icon name="lucideSearch" size="14" class="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted" />
                <input
                  type="text"
                  uiInput
                  size="sm"
                  class="pl-8!"
                  placeholder="Search accounts..."
                  [(ngModel)]="line.searchQuery"
                  (input)="onSearchInput(line)"
                  (focus)="onSearchFocus(line)"
                  (blur)="closeDropdown(line)"
                />
              </div>
              @if (line.dropdownOpen) {
                <div class="absolute inset-x-0 top-full z-1100 max-h-60 overflow-y-auto rounded-b-md border border-border bg-card shadow-lg">
                  @if (line.searching) {
                    <div class="p-2.5 text-center text-xs text-muted">Searching...</div>
                  } @else if (line.searchResults.length === 0) {
                    <div class="p-2.5 text-center text-xs text-muted">No accounts found</div>
                  } @else {
                    @for (account of line.searchResults; track account.id) {
                      <div class="flex cursor-pointer flex-col gap-0.5 border-b border-border px-2.5 py-2 last:border-b-0 hover:bg-row-hover" (mousedown)="selectAccount(line, account)">
                        <div class="flex items-baseline gap-2">
                          <span class="font-mono text-xs font-bold text-fg">{{ account.code }}</span>
                          <span class="truncate text-[0.72rem] text-muted">{{ account.name }}</span>
                        </div>
                        <div class="flex gap-2 text-[0.65rem]">
                          <span class="text-muted">{{ account.account_type_display }}</span>
                          <span class="font-mono font-semibold text-fg-secondary">{{ account.currency_code }} {{ account.balance }}</span>
                        </div>
                      </div>
                    }
                  }
                </div>
              }
            }
          </div>

          <!-- Amount -->
          <div class="flex min-w-0 flex-col">
            <label class="mb-0.5 text-[0.62rem] font-semibold uppercase tracking-wide text-muted">Amount</label>
            <input
              type="number"
              uiInput
              size="sm"
              class="font-mono"
              placeholder="0.00"
              min="0"
              step="0.01"
              [(ngModel)]="line.amount"
              (ngModelChange)="onAmountChange(line, 'DEBIT')"
            />
          </div>

          <!-- Description -->
          <div class="flex min-w-0 flex-col">
            <label class="mb-0.5 text-[0.62rem] font-semibold uppercase tracking-wide text-muted">Description</label>
            <input
              type="text"
              uiInput
              size="sm"
              placeholder="Optional line description"
              [(ngModel)]="line.description"
            />
          </div>

          <!-- Remove -->
          <button
            type="button"
            class="mb-0.5 flex size-8 cursor-pointer items-center justify-center self-end rounded border border-transparent bg-transparent text-muted transition-colors hover:border-danger-border hover:bg-danger-bg hover:text-danger max-md:justify-self-end"
            title="Remove line"
            (click)="removeDebitLine(line.uid)"
          >
            <ng-icon name="lucideX" size="14" />
          </button>

          <!-- Balance Warning -->
          @if (line.balanceWarning) {
            <div class="col-span-full mt-0.5 flex items-center gap-1 text-[0.7rem] font-semibold text-warning">
              <ng-icon name="lucideTriangleAlert" size="12" />
              <span>{{ line.balanceWarning }}</span>
            </div>
          }
        </div>
      }
    </div>

    <!-- Debit Totals -->
    @if (mapEntries(getDebitSectionTotals()).length > 0) {
      <div class="mt-3 flex flex-wrap gap-2 border-t border-dashed border-border pt-2.5">
        @for (entry of mapEntries(getDebitSectionTotals()); track entry[0]) {
          <span class="rounded border px-2.5 py-1 font-mono text-xs font-bold border-danger-border bg-danger-bg text-danger-fg">
            {{ entry[0] }} {{ formatNumber(entry[1]) }}
          </span>
        }
      </div>
    }
  </section>

  <!-- Credit Lines Section -->
  <section class="mb-4 rounded-lg border border-l-[3px] border-border border-l-success bg-card p-5">
    <div class="mb-3.5 flex items-center justify-between border-b border-border pb-2">
      <h3 class="m-0 text-sm font-bold uppercase tracking-wide text-success-fg">Credit Lines</h3>
      <button type="button" uiButton variant="outline" size="sm" (click)="addCreditLine()">
        <ng-icon name="lucidePlus" size="14" />
        <span>Add Line</span>
      </button>
    </div>

    <div class="flex flex-col gap-2.5">
      @for (line of creditLines(); track line.uid) {
        <div
          class="relative grid grid-cols-1 items-end gap-1.5 rounded-md border p-3 transition-colors md:grid-cols-[1fr_140px_1fr_32px] md:gap-2"
          [class]="line.balanceWarning ? 'border-warning bg-warning-bg' : 'border-border bg-surface-muted'"
        >
          <!-- Account Autocomplete -->
          <div class="relative flex min-w-0 flex-col">
            <label class="mb-0.5 text-[0.62rem] font-semibold uppercase tracking-wide text-muted">Account</label>
            @if (line.account) {
              <div class="flex min-h-[34px] flex-wrap items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1.5 text-xs">
                <span class="font-mono text-[0.72rem] font-bold text-fg">{{ line.account.code }}</span>
                <span class="max-w-30 truncate text-[0.7rem] text-muted">{{ line.account.name }}</span>
                <ui-badge variant="info" class="uppercase">{{ line.account.currency_code }}</ui-badge>
                <span
                  class="ml-auto font-mono text-[0.65rem]"
                  [class]="line.balanceWarning ? 'font-semibold text-warning' : 'text-muted'"
                >
                  Bal: {{ line.account.balance }}
                </span>
                <button type="button" class="ml-1 cursor-pointer border-0 bg-transparent p-0 leading-none text-muted hover:text-danger" (click)="clearAccount(line)">
                  <ng-icon name="lucideX" size="12" />
                </button>
              </div>
            } @else {
              <div class="relative">
                <ng-icon name="lucideSearch" size="14" class="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted" />
                <input
                  type="text"
                  uiInput
                  size="sm"
                  class="pl-8!"
                  placeholder="Search accounts..."
                  [(ngModel)]="line.searchQuery"
                  (input)="onSearchInput(line)"
                  (focus)="onSearchFocus(line)"
                  (blur)="closeDropdown(line)"
                />
              </div>
              @if (line.dropdownOpen) {
                <div class="absolute inset-x-0 top-full z-1100 max-h-60 overflow-y-auto rounded-b-md border border-border bg-card shadow-lg">
                  @if (line.searching) {
                    <div class="p-2.5 text-center text-xs text-muted">Searching...</div>
                  } @else if (line.searchResults.length === 0) {
                    <div class="p-2.5 text-center text-xs text-muted">No accounts found</div>
                  } @else {
                    @for (account of line.searchResults; track account.id) {
                      <div class="flex cursor-pointer flex-col gap-0.5 border-b border-border px-2.5 py-2 last:border-b-0 hover:bg-row-hover" (mousedown)="selectAccount(line, account)">
                        <div class="flex items-baseline gap-2">
                          <span class="font-mono text-xs font-bold text-fg">{{ account.code }}</span>
                          <span class="truncate text-[0.72rem] text-muted">{{ account.name }}</span>
                        </div>
                        <div class="flex gap-2 text-[0.65rem]">
                          <span class="text-muted">{{ account.account_type_display }}</span>
                          <span class="font-mono font-semibold text-fg-secondary">{{ account.currency_code }} {{ account.balance }}</span>
                        </div>
                      </div>
                    }
                  }
                </div>
              }
            }
          </div>

          <!-- Amount -->
          <div class="flex min-w-0 flex-col">
            <label class="mb-0.5 text-[0.62rem] font-semibold uppercase tracking-wide text-muted">Amount</label>
            <input
              type="number"
              uiInput
              size="sm"
              class="font-mono"
              placeholder="0.00"
              min="0"
              step="0.01"
              [(ngModel)]="line.amount"
              (ngModelChange)="onAmountChange(line, 'CREDIT')"
            />
          </div>

          <!-- Description -->
          <div class="flex min-w-0 flex-col">
            <label class="mb-0.5 text-[0.62rem] font-semibold uppercase tracking-wide text-muted">Description</label>
            <input
              type="text"
              uiInput
              size="sm"
              placeholder="Optional line description"
              [(ngModel)]="line.description"
            />
          </div>

          <!-- Remove -->
          <button
            type="button"
            class="mb-0.5 flex size-8 cursor-pointer items-center justify-center self-end rounded border border-transparent bg-transparent text-muted transition-colors hover:border-danger-border hover:bg-danger-bg hover:text-danger max-md:justify-self-end"
            title="Remove line"
            (click)="removeCreditLine(line.uid)"
          >
            <ng-icon name="lucideX" size="14" />
          </button>

          <!-- Balance Warning -->
          @if (line.balanceWarning) {
            <div class="col-span-full mt-0.5 flex items-center gap-1 text-[0.7rem] font-semibold text-warning">
              <ng-icon name="lucideTriangleAlert" size="12" />
              <span>{{ line.balanceWarning }}</span>
            </div>
          }
        </div>
      }
    </div>

    <!-- Credit Totals -->
    @if (mapEntries(getCreditSectionTotals()).length > 0) {
      <div class="mt-3 flex flex-wrap gap-2 border-t border-dashed border-border pt-2.5">
        @for (entry of mapEntries(getCreditSectionTotals()); track entry[0]) {
          <span class="rounded border px-2.5 py-1 font-mono text-xs font-bold border-success-border bg-success-bg text-success-fg">
            {{ entry[0] }} {{ formatNumber(entry[1]) }}
          </span>
        }
      </div>
    }
  </section>

  <!-- Balance Validation Panel -->
  <section
    class="mb-4 rounded-lg border-2 px-5 py-4 transition-colors"
    [class]="isAllBalanced() ? 'border-success-border bg-success-bg' : (hasAnyLines() ? 'border-danger-border bg-danger-bg' : 'border-border bg-card')"
  >
    <div class="mb-3 flex items-center justify-between">
      <h3 class="m-0 text-sm font-bold uppercase tracking-wide text-muted">Balance Check</h3>
      <button type="button" uiButton variant="outline" size="sm" title="Refresh account balances" (click)="refreshBalances()">
        <ng-icon name="lucideRefreshCw" size="14" />
        <span>Refresh Balances</span>
      </button>
    </div>

    @if (getCurrencyBalances().length === 0) {
      <p class="m-0 text-sm text-muted">Add debit and credit lines to see balance status.</p>
    } @else {
      <div class="flex flex-col gap-2">
        @for (bal of getCurrencyBalances(); track bal.currency) {
          <div
            class="flex items-center gap-3 rounded-md border bg-card px-3.5 py-2.5"
            [class]="bal.isBalanced ? 'border-success-border' : 'border-danger-border'"
          >
            <span class="min-w-10 font-mono text-sm font-bold text-fg">{{ bal.currency }}</span>
            <div class="flex flex-1 flex-col gap-1 md:flex-row md:gap-4">
              <span class="flex min-w-20 flex-col items-center">
                <span class="text-[0.6rem] font-semibold uppercase text-muted">Debits</span>
                <span class="font-mono text-sm font-bold text-danger-fg">{{ formatNumber(bal.totalDebits) }}</span>
              </span>
              <span class="flex min-w-20 flex-col items-center">
                <span class="text-[0.6rem] font-semibold uppercase text-muted">Credits</span>
                <span class="font-mono text-sm font-bold text-success-fg">{{ formatNumber(bal.totalCredits) }}</span>
              </span>
              <span class="flex min-w-20 flex-col items-center">
                <span class="text-[0.6rem] font-semibold uppercase text-muted">Diff</span>
                <span class="font-mono text-sm font-bold text-fg">{{ formatNumber(bal.difference) }}</span>
              </span>
            </div>
            <span class="flex items-center">
              @if (bal.isBalanced) {
                <ng-icon name="lucideCheck" size="16" class="text-success" />
              } @else {
                <ng-icon name="lucideX" size="16" class="text-danger" />
              }
            </span>
          </div>
        }
      </div>
    }
  </section>

  <!-- Submit -->
  <div class="flex flex-col justify-end gap-3 py-4 sm:flex-row">
    <a uiButton variant="outline" [routerLink]="shellRoutes.commands('manage', entityKey)">Cancel</a>
    <button type="button" uiButton [disabled]="!canSubmit()" (click)="submit()">
      @if (submitting()) {
        <ui-spinner size="sm" />
      } @else {
        <ng-icon name="lucideCheck" size="16" />
      }
      <span>Submit Operation</span>
    </button>
  </div>
</div>
